import { AdminPage, AdminPageHeader } from '../../components/layout/AdminPage';
import { MemberLevelGrants } from './MemberLevelGrants';
import { useMemberDeactivationConfirm } from './MemberDeactivationConfirm';
/**
 * @page MemberFormPage
 * @description 新增 / 編輯會員表單（UI-3-2）
 * 路由：/members/create  → 新增
 *       /members/:id/edit → 編輯
 */
import { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api } from '../../lib/api.ts';
import type { MemberDto, MemberLevelDto, MemberStatus } from '@pokerroom/types';

interface DuplicateMatch {
  id: string;
  memberNo: string;
  nickname: string;
  matchField: 'phone' | 'email';
}
interface DuplicateResult {
  hasDuplicate: boolean;
  phoneUnavailable: boolean;
  matches: DuplicateMatch[];
}

// ============================================================
// Schema — validation messages are overridden via i18n in the component
// ============================================================
const createSchema = (nicknameMsg: string, emailMsg: string) =>
  z.object({
    nickname: z.string().min(1, nicknameMsg).max(50),
    phone: z.string().max(20).optional().or(z.literal('')),
    email: z.string().email(emailMsg).max(100).optional().or(z.literal('')),
    levelId: z.string().optional(),
    validity: z.enum(['WEEK', 'MONTH', 'NONE']).optional(),
    reason: z.string().max(500).optional(),
    status: z.enum(['ACTIVE', 'INACTIVE', 'BLACKLISTED']).optional(),
    note: z.string().max(500).optional().or(z.literal('')),
  });
type FormData = z.infer<ReturnType<typeof createSchema>>;

// ============================================================
// Component
// ============================================================
export default function MemberFormPage() {
  const { t } = useTranslation(['member', 'common']);
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;
  const navigate = useNavigate();
  const qc = useQueryClient();
  const deactivation = useMemberDeactivationConfirm(id);

  const { data: levels = [] } = useQuery<MemberLevelDto[]>({
    queryKey: ['member-levels'],
    queryFn: () => api.get<MemberLevelDto[]>('/members/levels'),
  });

  const schema = createSchema(t('formNicknameRequired'), t('formEmailInvalid'));

  const activeLevels = levels.filter((l) => l.isActive);
  const defaultLevel = activeLevels.find((l) => l.isDefault) ?? activeLevels[0];

  const STATUS_OPTS: { value: MemberStatus; label: string }[] = [
    { value: 'ACTIVE', label: t('status_ACTIVE') },
    { value: 'INACTIVE', label: t('status_INACTIVE') },
    { value: 'BLACKLISTED', label: t('status_BLACKLISTED') },
  ];

  const {
    register,
    watch,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: { levelId: defaultLevel?.id ?? '', status: 'ACTIVE' },
  });

  // 編輯時載入現有資料
  const { data: member } = useQuery<MemberDto>({
    queryKey: ['member', id],
    queryFn: () => api.get<MemberDto>(`/members/${id!}`),
    enabled: isEdit,
  });

  useEffect(() => {
    if (member) {
      reset({
        nickname: member.nickname,
        phone: member.phone ?? '',
        email: member.email ?? '',
        levelId: member.levelId,
        status: member.status,
        note: member.note ?? '',
      });
    }
  }, [member, reset]);

  // Set default level when levels load (create mode)
  useEffect(() => {
    if (!isEdit && defaultLevel) {
      reset((prev) => ({ ...prev, levelId: defaultLevel.id }));
    }
  }, [defaultLevel, isEdit, reset]);

  // Duplicate detection
  const [phoneMatches, setPhoneMatches] = useState<DuplicateMatch[]>([]);
  const [emailMatches, setEmailMatches] = useState<DuplicateMatch[]>([]);
  const [phoneUnavailable, setPhoneUnavailable] = useState(false);
  const duplicateRequests = useRef({ phone: 0, email: 0 });
  const duplicateMatches = [
    ...phoneMatches,
    ...emailMatches.filter(
      (emailMatch) => !phoneMatches.some((phoneMatch) => phoneMatch.id === emailMatch.id),
    ),
  ];
  const checkDuplicate = useCallback(
    async (phone?: string, email?: string) => {
      const field = phone !== undefined ? 'phone' : 'email';
      const requestId = ++duplicateRequests.current[field];
      if (!phone && !email) {
        if (field === 'phone') {
          setPhoneMatches([]);
          setPhoneUnavailable(false);
        } else {
          setEmailMatches([]);
        }
        return;
      }
      try {
        const params = new URLSearchParams();
        if (phone) params.set('phone', phone);
        if (email) params.set('email', email);
        if (isEdit && id) params.set('excludeId', id);
        const result = await api.get<DuplicateResult>(
          `/members/check-duplicate?${params.toString()}`,
        );
        if (requestId !== duplicateRequests.current[field]) return;
        if (field === 'phone') {
          setPhoneMatches(result.matches);
          setPhoneUnavailable(result.phoneUnavailable);
        } else {
          setEmailMatches(result.matches);
        }
      } catch (err) {
        if (requestId !== duplicateRequests.current[field]) return;
        console.warn('Duplicate check failed:', (err as Error).message);
        if (field === 'phone') {
          setPhoneMatches([]);
          setPhoneUnavailable(false);
        } else {
          setEmailMatches([]);
        }
      }
    },
    [isEdit, id],
  );

  const createMutation = useMutation({
    mutationFn: (data: FormData) => api.post<MemberDto>('/members', data),
    onSuccess: (newMember) => {
      qc.invalidateQueries({ queryKey: ['members'] });
      navigate(`/members/${newMember.id}`);
    },
    onError: (err) => {
      console.warn('[MemberForm] create failed:', (err as Error).message);
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: FormData) => api.patch<MemberDto>(`/members/${id!}`, data),
    onSuccess: (updatedMember) => {
      qc.setQueryData(['member', id], updatedMember);
      qc.invalidateQueries({ queryKey: ['members'] });
      qc.invalidateQueries({ queryKey: ['member', id] });
      navigate(`/members/${id!}`);
    },
    onError: (err) => {
      console.warn('[MemberForm] update failed:', (err as Error).message);
    },
  });

  const submitting = useRef(false);
  const onSubmit = async (data: FormData) => {
    if (submitting.current) return;
    submitting.current = true;
    try {
      if (
        isEdit &&
        member?.associationMode &&
        member.status === 'ACTIVE' &&
        data.status &&
        data.status !== 'ACTIVE' &&
        !(await deactivation.ask())
      )
        return;
      const payload = {
        nickname: data.nickname,
        phone: data.phone || undefined,
        email: data.email || undefined,
        note: data.note || undefined,
        levelId: data.levelId || undefined,
        validity: data.validity,
        reason: data.reason,
        ...(isEdit ? { status: data.status } : {}),
      };
      if (isEdit) {
        await updateMutation.mutateAsync(payload);
      } else {
        await createMutation.mutateAsync(payload);
      }
    } catch {
      // Mutation state keeps the actionable error and draft available for retry.
    } finally {
      submitting.current = false;
    }
  };

  const serverError = (createMutation.error ?? updateMutation.error) as {
    response?: { data?: { message?: string } };
  } | null;

  return (
    <AdminPage
      contentClassName="admin-content-short"
      header={
        <AdminPageHeader
          title={isEdit ? t('editMember') : t('createMember')}
          back={
            <div className="flex items-center gap-2 text-sm text-base-content/50">
              <Link to="/members" className="hover:text-primary">
                {t('memberList')}
              </Link>
              <span>&rsaquo;</span>
              {isEdit && member && (
                <>
                  <Link to={`/members/${id}`} className="hover:text-primary">
                    {member.nickname}
                  </Link>
                  <span>&rsaquo;</span>
                </>
              )}
              <span className="text-base-content">
                {isEdit ? t('editMember') : t('createMember')}
              </span>
            </div>
          }
        />
      }
    >
      {isEdit && (
        <div className="mb-5 rounded-lg border border-warning/40 bg-warning/10 p-4 text-sm">
          <p className="font-semibold text-warning">{t('sharedProfileWarningTitle')}</p>
          <p className="mt-1 text-base-content/70">{t('sharedProfileWarningBody')}</p>
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
        <div className="bg-base-200 rounded-xl border border-base-300 p-5 space-y-4">
          {/* 暱稱 */}
          <div>
            <label className="block text-sm font-medium text-base-content/70 mb-1.5">
              {t('nickname')} *
            </label>
            <input
              {...register('nickname')}
              className="w-full px-3 py-2.5 rounded-lg border border-base-300 bg-base-300 text-base-content text-sm focus:outline-hidden focus:ring-2 focus:ring-primary"
              placeholder={t('formNicknamePlaceholder')}
            />
            {errors.nickname && (
              <p className="text-xs text-error mt-1">{errors.nickname.message}</p>
            )}
          </div>

          {/* 電話 */}
          <div>
            <label className="block text-sm font-medium text-base-content/70 mb-1.5">
              {t('phone')}
            </label>
            <input
              {...register('phone', {
                onBlur: (e: React.FocusEvent<HTMLInputElement>) => {
                  const val = e.target.value;
                  void checkDuplicate(val, undefined);
                },
              })}
              type="tel"
              className="w-full px-3 py-2.5 rounded-lg border border-base-300 bg-base-300 text-base-content text-sm focus:outline-hidden focus:ring-2 focus:ring-primary"
              placeholder={t('formPhonePlaceholder')}
            />
            {errors.phone && <p className="text-xs text-error mt-1">{errors.phone.message}</p>}
          </div>

          {/* Email */}
          <div>
            <label className="block text-sm font-medium text-base-content/70 mb-1.5">Email</label>
            <input
              {...register('email', {
                onBlur: (e: React.FocusEvent<HTMLInputElement>) => {
                  const val = e.target.value;
                  void checkDuplicate(undefined, val);
                },
              })}
              type="email"
              className="w-full px-3 py-2.5 rounded-lg border border-base-300 bg-base-300 text-base-content text-sm focus:outline-hidden focus:ring-2 focus:ring-primary"
              placeholder="member@example.com"
            />
            {errors.email && <p className="text-xs text-error mt-1">{errors.email.message}</p>}
          </div>

          {/* Duplicate Warning */}
          {(duplicateMatches.length > 0 || phoneUnavailable) && (
            <div className="px-4 py-3 bg-warning/10 border border-warning/30 rounded-lg text-sm text-warning">
              {phoneUnavailable && (
                <p className="font-medium">
                  {t('phoneUnavailable', { defaultValue: '電話不可使用' })}
                </p>
              )}
              {duplicateMatches.length > 0 && (
                <p className="font-medium mb-1">
                  {t('duplicateWarning', { defaultValue: '可能重複的會員：' })}
                </p>
              )}
              <ul className="list-disc list-inside">
                {duplicateMatches.map((m) => (
                  <li key={m.id}>
                    <Link to={`/members/${m.id}`} className="underline hover:text-warning/80">
                      {m.nickname} ({m.memberNo})
                    </Link>
                    {' — '}
                    {m.matchField === 'phone' ? t('phone') : 'Email'}{' '}
                    {t('duplicateMatch', { defaultValue: '相同' })}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {!isEdit &&
            levels.some((l) => l.associationMode) &&
            watch('levelId') &&
            watch('levelId') !== defaultLevel?.id && (
              <div className="space-y-2">
                <label>
                  {t('levelValidity')}
                  <select
                    {...register('validity')}
                    required
                    className="px-3 py-2.5 rounded-lg border border-base-300 bg-base-300 text-base-content text-sm focus:outline-hidden focus:ring-2 focus:ring-primary disabled:opacity-50 w-full"
                  >
                    <option value="">{t('levelValidity')}</option>
                    {(['WEEK', 'MONTH', 'NONE'] as const).map((v) => (
                      <option key={v} value={v}>
                        {t(`levelValidity${v}`)}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  {t('levelGrantReason')}
                  <input
                    {...register('reason')}
                    required
                    maxLength={500}
                    className="px-3 py-2.5 rounded-lg border border-base-300 bg-base-300 text-base-content text-sm focus:outline-hidden focus:ring-2 focus:ring-primary disabled:opacity-50 w-full"
                  />
                </label>
              </div>
            )}
          {/* 等級 + 狀態 */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-base-content/70 mb-1.5">
                {t('memberTier')}
              </label>
              <select
                {...register('levelId')}
                disabled={isEdit && levels.some((l) => l.associationMode)}
                className="w-full px-3 py-2.5 rounded-lg border border-base-300 bg-base-300 text-base-content text-sm focus:outline-hidden focus:ring-2 focus:ring-primary"
              >
                {activeLevels.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.displayName}
                  </option>
                ))}
              </select>
            </div>
            {isEdit && (
              <div>
                <label className="block text-sm font-medium text-base-content/70 mb-1.5">
                  {t('status')}
                </label>
                <select
                  {...register('status')}
                  className="w-full px-3 py-2.5 rounded-lg border border-base-300 bg-base-300 text-base-content text-sm focus:outline-hidden focus:ring-2 focus:ring-primary"
                >
                  {STATUS_OPTS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {/* 備註 */}
          <div>
            <label className="block text-sm font-medium text-base-content/70 mb-1.5">
              {t('note')}
            </label>
            <textarea
              {...register('note')}
              rows={3}
              className="w-full px-3 py-2.5 rounded-lg border border-base-300 bg-base-300 text-base-content text-sm focus:outline-hidden focus:ring-2 focus:ring-primary resize-none"
              placeholder={t('formNotePlaceholder')}
            />
          </div>
        </div>

        {/* Server Error */}
        {serverError && (
          <div className="px-4 py-3 bg-error/10 border border-error/30 rounded-lg text-sm text-error">
            {serverError.response?.data?.message ?? t('formErrorRetry')}
          </div>
        )}

        <div className="flex justify-end gap-3">
          <Link
            to={isEdit ? `/members/${id}` : '/members'}
            className="inline-flex min-h-11 items-center px-4 py-2 rounded-lg border border-base-300 text-sm text-base-content hover:bg-base-300 transition-colors"
          >
            {t('common:cancel')}
          </Link>
          <button
            type="submit"
            disabled={isSubmitting || createMutation.isPending || updateMutation.isPending}
            className="min-h-11 px-6 py-2 rounded-lg bg-primary text-primary-content text-sm font-medium hover:bg-primary/90 disabled:opacity-50 transition-colors"
          >
            {isSubmitting || createMutation.isPending || updateMutation.isPending
              ? t('formSaving')
              : isEdit
                ? t('formSaveChanges')
                : t('formCreate')}
          </button>
        </div>
      </form>
      {!isEdit &&
        levels.some((l) => l.associationMode) &&
        watch('levelId') &&
        watch('levelId') !== defaultLevel?.id && (
          <p className="text-sm">{t('firstLevelGrantHint')}</p>
        )}
      {isEdit && levels.some((l) => l.associationMode) && (
        <MemberLevelGrants memberId={id!} levels={levels} />
      )}
      {deactivation.dialog}
    </AdminPage>
  );
}
